import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define pricing calculator types
export type ServiceType = 'training' | 'onboarding' | 'automation' | 'development' | 'optimization';
export type TeamSize = 'individual' | 'small' | 'medium' | 'large' | 'enterprise';
export type Duration = 'short' | 'medium' | 'long';
export type SupportLevel = 'basic' | 'standard' | 'premium';
export type CustomizationLevel = 'low' | 'medium' | 'high';

export interface PricingParams {
  serviceType: ServiceType;
  teamSize: TeamSize;
  duration: Duration;
  supportLevel: SupportLevel;
  customizationLevel: CustomizationLevel;
  includeTraining: boolean;
  includeMaintenancePlan: boolean;
  includePrioritySupport: boolean;
}

export interface PricingEstimate {
  baseCost: number;
  teamSizeCost: number;
  durationCost: number;
  supportCost: number;
  customizationCost: number;
  additionalServicesCost: number;
  totalCost: number;
  estimatedTimelineWeeks: number;
  recommendedServices: string[];
}

// Default values
const defaultPricingParams: PricingParams = {
  serviceType: 'training',
  teamSize: 'small',
  duration: 'medium',
  supportLevel: 'standard',
  customizationLevel: 'medium',
  includeTraining: false,
  includeMaintenancePlan: false,
  includePrioritySupport: false,
};

interface PricingContextType {
  params: PricingParams;
  updateParams: (params: Partial<PricingParams>) => void;
  estimate: PricingEstimate | null;
  generateEstimate: () => void;
  resetCalculator: () => void;
}

// Create the context
const PricingContext = createContext<PricingContextType | undefined>(undefined);

// Provider component
interface PricingProviderProps {
  children: ReactNode;
}

export const PricingProvider: React.FC<PricingProviderProps> = ({ children }) => {
  const [params, setParams] = useState<PricingParams>(defaultPricingParams);
  const [estimate, setEstimate] = useState<PricingEstimate | null>(null);

  // Update parameters with partial data
  const updateParams = (newParams: Partial<PricingParams>) => {
    setParams((prev) => ({ ...prev, ...newParams }));
  };

  // Reset to default
  const resetCalculator = () => {
    setParams(defaultPricingParams);
    setEstimate(null);
  };

  // Calculate pricing estimate based on parameters
  const generateEstimate = () => {
    const {
      serviceType,
      teamSize,
      duration,
      supportLevel,
      customizationLevel,
      includeTraining,
      includeMaintenancePlan,
      includePrioritySupport,
    } = params;

    // Base costs for different service types
    const baseCosts = {
      training: 2000,
      onboarding: 5000,
      automation: 7500,
      development: 15000,
      optimization: 10000,
    };

    // Team size multipliers
    const teamSizeMultipliers = {
      individual: 0.6,
      small: 1.0,
      medium: 1.5,
      large: 2.2,
      enterprise: 3.5,
    };

    // Duration multipliers
    const durationMultipliers = {
      short: 0.7,
      medium: 1.0,
      long: 1.5,
    };

    // Support level costs
    const supportCosts = {
      basic: 0,
      standard: 1000,
      premium: 3000,
    };

    // Customization level multipliers
    const customizationMultipliers = {
      low: 0.8,
      medium: 1.0,
      high: 1.5,
    };

    // Calculate base service cost with team size and duration
    const baseCost = baseCosts[serviceType];
    const teamSizeCost = baseCost * teamSizeMultipliers[teamSize] - baseCost;
    const durationCost = baseCost * durationMultipliers[duration] - baseCost;
    
    // Calculate support and customization costs
    const supportCost = supportCosts[supportLevel];
    const customizationCost = baseCost * customizationMultipliers[customizationLevel] - baseCost;
    
    // Calculate additional services cost
    let additionalServicesCost = 0;
    if (includeTraining) additionalServicesCost += 1500;
    if (includeMaintenancePlan) additionalServicesCost += 2000;
    if (includePrioritySupport) additionalServicesCost += 1000;
    
    // Calculate total cost
    const totalCost = 
      baseCost + 
      teamSizeCost + 
      durationCost + 
      supportCost + 
      customizationCost + 
      additionalServicesCost;
    
    // Estimate project timeline in weeks
    const baseTimelineWeeks = {
      training: 2,
      onboarding: 4,
      automation: 6,
      development: 12,
      optimization: 8,
    };
    
    const timelineMultiplier = 
      (teamSize === 'enterprise' ? 1.5 : 1) * 
      (customizationLevel === 'high' ? 1.3 : 1) *
      (duration === 'long' ? 1.5 : duration === 'short' ? 0.7 : 1);
    
    const estimatedTimelineWeeks = Math.round(baseTimelineWeeks[serviceType] * timelineMultiplier);
    
    // Generate recommended services based on selections
    let recommendedServices: string[] = [];
    
    if (serviceType === 'training') {
      recommendedServices.push('AI Training & Education Program');
      if (includeMaintenancePlan) {
        recommendedServices.push('Ongoing Support & Knowledge Updates');
      }
    } else if (serviceType === 'onboarding') {
      recommendedServices.push('Business AI Onboarding Package');
      if (includeTraining) {
        recommendedServices.push('Team Training Sessions');
      }
    } else if (serviceType === 'automation') {
      recommendedServices.push('Workflow Automation Solution');
      if (customizationLevel === 'high') {
        recommendedServices.push('Custom Integration Development');
      }
    } else if (serviceType === 'development') {
      recommendedServices.push('Custom AI Solution Development');
      if (includeMaintenancePlan) {
        recommendedServices.push('Ongoing Maintenance & Updates');
      }
      if (includePrioritySupport) {
        recommendedServices.push('24/7 Priority Support');
      }
    } else if (serviceType === 'optimization') {
      recommendedServices.push('AI System Optimization');
      if (includeTraining) {
        recommendedServices.push('System Administration Training');
      }
    }
    
    setEstimate({
      baseCost,
      teamSizeCost,
      durationCost,
      supportCost,
      customizationCost,
      additionalServicesCost,
      totalCost,
      estimatedTimelineWeeks,
      recommendedServices,
    });
  };

  return (
    <PricingContext.Provider value={{
      params,
      updateParams,
      estimate,
      generateEstimate,
      resetCalculator,
    }}>
      {children}
    </PricingContext.Provider>
  );
};

// Custom hook to use the pricing context
export const usePricing = () => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};
